import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import './Skills.css';

const Skills = () => {
  const { t } = useTranslation();

  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };
  
  const skillCategories = [
    {
      title: t('skills.categories.frontend'),
      icon: '🎨',
      skills: [
        { name: 'JavaScript', level: 90, icon: '📜', definition: 'Modern ES6+, async programming, DOM manipulation' },
        { name: 'React', level: 90, icon: '⚛️', definition: 'Component-based UI library with hooks and context' },
        { name: 'React Redux', level: 85, icon: '🔄', definition: 'State management with actions and reducers' },
        { name: 'React Router', level: 85, icon: '🛣️', definition: 'Client-side routing for single-page apps' },
        { name: 'React Bootstrap', level: 85, icon: '🎨', definition: 'Responsive layouts with Bootstrap components' },
        { name: 'Formik & Yup', level: 80, icon: '📝', definition: 'Form handling with validation' },
        { name: 'React Konva', level: 80, icon: '🎯', definition: 'Canvas graphics and animations' },
        { name: 'Axios', level: 85, icon: '🔌', definition: 'Promise-based HTTP client' }
      ]
    },
    {
      title: t('skills.categories.backend'),
      icon: '⚙️',
      skills: [
        { name: 'Node.js', level: 90, icon: '💻', definition: 'Server-side JavaScript runtime' },
        { name: 'Express.js', level: 90, icon: '🚀', definition: 'Fast, minimalist web framework' },
        { name: 'JWT', level: 85, icon: '🔑', definition: 'Secure authentication and authorization' },
        { name: 'Mongoose', level: 85, icon: '📦', definition: 'MongoDB object modeling tool' },
        { name: 'Nodemailer', level: 80, icon: '📧', definition: 'Email sending with templates' },
        { name: 'Multer', level: 80, icon: '📁', definition: 'File upload middleware' },
        { name: 'FastQ', level: 80, icon: '⚡', definition: 'Job queues and background processing' },
        { name: 'Cloudinary', level: 85, icon: '☁️', definition: 'Cloud media management' }
      ]
    },
    {
      title: t('skills.categories.cloud'),
      icon: '☁️',
      skills: [
        { name: 'AWS EC2', level: 85, icon: '🖥️', definition: 'Virtual servers in the cloud' },
        { name: 'AWS RDS', level: 85, icon: '💾', definition: 'Managed relational databases' },
        { name: 'AWS S3', level: 85, icon: '📦', definition: 'Scalable object storage' },
        { name: 'AWS Route53', level: 80, icon: '🌐', definition: 'DNS and domain management' },
        { name: 'GCP VM', level: 80, icon: '🔧', definition: 'Google Cloud compute instances' },
        { name: 'GCP SQL', level: 80, icon: '📊', definition: 'Managed MySQL and PostgreSQL' },
        { name: 'Load Balancing', level: 85, icon: '⚖️', definition: 'Traffic distribution and scaling' },
        { name: 'Git', level: 90, icon: '📚', definition: 'Version control and collaboration' }
      ]
    },
    {
      title: t('skills.categories.database'),
      icon: '🗄️',
      skills: [
        { name: 'MongoDB', level: 90, icon: '🍃', definition: 'NoSQL document database' },
        { name: 'MySQL', level: 90, icon: '📊', definition: 'Relational database system' },
        { name: 'Moment.js', level: 85, icon: '⏰', definition: 'Date/time parsing and formatting' },
        { name: 'Lodash', level: 85, icon: '🔧', definition: 'JavaScript utility library' },
        { name: 'Nodemon', level: 85, icon: '🔄', definition: 'Auto-restart for development' },
        { name: 'Forever', level: 80, icon: '♾️', definition: 'Process manager for Node.js' },
        { name: 'Sharp', level: 80, icon: '🖼️', definition: 'High-performance image processing' },
        { name: 'Bcrypt', level: 85, icon: '🔒', definition: 'Password hashing and security' }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <section id="skills" className="skills">
 
      <div className="skills-content">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          dangerouslySetInnerHTML={createMarkup(t('skills.title'))}
        />
        <motion.div 
          className="skills-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {skillCategories.map((category, index) => (
            <motion.div 
              key={index} 
              className="skill-category"
              variants={itemVariants}
            >
              <div className="category-header">
                <span className="category-icon">{category.icon}</span>
                <h3 dangerouslySetInnerHTML={createMarkup(category.title)} />
              </div>
              <div className="skill-list">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div 
                    key={skillIndex} 
                    className="skill-item"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="skill-info">
                      <div className="skill-name-container">
                        <span className="skill-icon">{skill.icon}</span>
                        <div className="skill-text">
                          <span className="skill-name" dangerouslySetInnerHTML={createMarkup(skill.name)} />
                          <span className="skill-definition">{skill.definition}</span>
                        </div>
                      </div>
                      <span className="skill-percentage">{skill.level}%</span>
                    </div>
                    <div className="skill-bar-container">
                      <motion.div 
                        className="skill-bar"
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      >
                        <div className="skill-bar-fill"></div>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
